import { Suspense, lazy } from 'react';
import { Navigate, useRoutes, useLocation } from 'react-router-dom';
// layouts
import LogoOnlyLayout from 'layouts/LogoOnlyLayout';
import DashboardLayout from 'layouts/dashboard';
// guards
import GuestGuard from 'guards/GuestGuard';
import AuthGuard from 'guards/AuthGuard';
import LoadingScreen from 'components/LoadingScreen';

// ----------------------------------------------------------------------

const Loadable = (Component) =>
  function LoadableComponent(props) {
    const { pathname } = useLocation();
    const isApp = pathname.includes('/app');

    return (
      <Suspense
        fallback={
          <LoadingScreen
            sx={{
              ...(!isApp && {
                top: 0,
                left: 0,
                width: 1,
                zIndex: 9999,
                position: 'fixed'
              })
            }}
          />
        }
      >
        <Component {...props} />
      </Suspense>
    );
  };

const Login = Loadable(lazy(() => import('pages/authentication/login/Login')));
const Register = Loadable(lazy(() => import('pages/authentication/Register')));
const ContractSignPublic = Loadable(lazy(() => import('pages/public/ContractSignPublic')));
const ReceivablePublic = Loadable(lazy(() => import('pages/public/ReceivablePublic')));

const AdminDashboard = Loadable(lazy(() => import('pages/admin/Dashboard')));
const ClientDashboard = Loadable(lazy(() => import('pages/admin/ClientDashboard')));
const Clients = Loadable(lazy(() => import('pages/admin/Clients')));
const Cartera = Loadable(lazy(() => import('pages/admin/Cartera')));
const CarteraSettings = Loadable(lazy(() => import('pages/admin/CarteraSettings')));
const ClientForm = Loadable(lazy(() => import('pages/admin/ClientForm')));
const Countries = Loadable(lazy(() => import('pages/admin/Countries')));
const CountryForm = Loadable(lazy(() => import('pages/admin/CountryForm')));
const States = Loadable(lazy(() => import('pages/admin/States')));
const StateForm = Loadable(lazy(() => import('pages/admin/StateForm')));
const Cities = Loadable(lazy(() => import('pages/admin/Cities')));
const CityForm = Loadable(lazy(() => import('pages/admin/CityForm')));
const Currencies = Loadable(lazy(() => import('pages/admin/Currencies')));
const CurrencyForm = Loadable(lazy(() => import('pages/admin/CurrencyForm')));
const PriceLists = Loadable(lazy(() => import('pages/admin/PriceLists')));
const PriceListForm = Loadable(lazy(() => import('pages/admin/PriceListForm')));
const Products = Loadable(lazy(() => import('pages/admin/Products')));
const ProductForm = Loadable(lazy(() => import('pages/admin/ProductForm')));
const Quotes = Loadable(lazy(() => import('pages/admin/Quotes')));
const QuoteForm = Loadable(lazy(() => import('pages/admin/QuoteForm')));
const QuoteProposalTemplates = Loadable(lazy(() => import('pages/admin/QuoteProposalTemplates')));
const QuoteProposalTemplateForm = Loadable(lazy(() => import('pages/admin/QuoteProposalTemplateForm')));
const QuoteVariableDefinitions = Loadable(lazy(() => import('pages/admin/QuoteVariableDefinitions')));
const QuoteVariableDefinitionForm = Loadable(lazy(() => import('pages/admin/QuoteVariableDefinitionForm')));
const QuoteFormulaDefinitions = Loadable(lazy(() => import('pages/admin/QuoteFormulaDefinitions')));
const QuoteFormulaDefinitionForm = Loadable(lazy(() => import('pages/admin/QuoteFormulaDefinitionForm')));
const ClientQuoteVariableValues = Loadable(lazy(() => import('pages/admin/ClientQuoteVariableValues')));
const Industries = Loadable(lazy(() => import('pages/admin/Industries')));
const IndustryForm = Loadable(lazy(() => import('pages/admin/IndustryForm')));
const WorkAreas = Loadable(lazy(() => import('pages/admin/WorkAreas')));
const WorkAreaForm = Loadable(lazy(() => import('pages/admin/WorkAreaForm')));
const KanbanPriorities = Loadable(lazy(() => import('pages/admin/KanbanPriorities')));
const KanbanPriorityForm = Loadable(lazy(() => import('pages/admin/KanbanPriorityForm')));
const CalendarCategories = Loadable(lazy(() => import('pages/admin/CalendarCategories')));
const CalendarCategoryForm = Loadable(lazy(() => import('pages/admin/CalendarCategoryForm')));
const AreaCapacityCalendar = Loadable(lazy(() => import('pages/admin/AreaCapacityCalendar')));
const AreaCalendar = Loadable(lazy(() => import('pages/admin/AreaCalendar')));
const AreaPostIts = Loadable(lazy(() => import('pages/admin/AreaPostIts')));
const FormDesignerForms = Loadable(lazy(() => import('pages/admin/FormDesignerForms')));
const FormDesignerForm = Loadable(lazy(() => import('pages/admin/FormDesignerForm')));
const Processes = Loadable(lazy(() => import('pages/admin/Processes')));
const ProcessForm = Loadable(lazy(() => import('pages/admin/ProcessForm')));
const RequirementTemplates = Loadable(lazy(() => import('pages/admin/RequirementTemplates')));
const RequirementTemplateForm = Loadable(lazy(() => import('pages/admin/RequirementTemplateForm')));
const Requirements = Loadable(lazy(() => import('pages/admin/Requirements')));
const RequirementDetail = Loadable(lazy(() => import('pages/admin/RequirementDetail')));
const ClientStatuses = Loadable(lazy(() => import('pages/admin/ClientStatuses')));
const ClientStatusForm = Loadable(lazy(() => import('pages/admin/ClientStatusForm')));
const DocumentTypes = Loadable(lazy(() => import('pages/admin/DocumentTypes')));
const DocumentTypeForm = Loadable(lazy(() => import('pages/admin/DocumentTypeForm')));
const CompanyProfileForm = Loadable(lazy(() => import('pages/admin/CompanyProfileForm')));
const StorageConfigPage  = Loadable(lazy(() => import('pages/admin/storage/StorageConfigPage')));
const KanbanCommercial = Loadable(lazy(() => import('pages/admin/KanbanCommercial')));
const KanbanMyWeek = Loadable(lazy(() => import('pages/admin/KanbanMyWeek')));
const KanbanCardTemplates = Loadable(lazy(() => import('pages/admin/KanbanCardTemplates')));
const KanbanCardTemplateForm = Loadable(lazy(() => import('pages/admin/KanbanCardTemplateForm')));
const ChatInbox = Loadable(lazy(() => import('pages/admin/ChatInbox')));
const DeveloperReleases = Loadable(lazy(() => import('pages/admin/DeveloperReleases')));
const DeveloperEnpisoApi = Loadable(lazy(() => import('pages/admin/DeveloperEnpisoApi')));
const ReleaseSystems = Loadable(lazy(() => import('pages/admin/ReleaseSystems')));
const ReleaseSystemForm = Loadable(lazy(() => import('pages/admin/ReleaseSystemForm')));
const InfraDashboard = Loadable(lazy(() => import('pages/admin/InfraDashboard')));
const InfraServers = Loadable(lazy(() => import('pages/admin/InfraServers')));
const InfraServerForm = Loadable(lazy(() => import('pages/admin/InfraServerForm')));
const InfraRuntimeUnits = Loadable(lazy(() => import('pages/admin/InfraRuntimeUnits')));
const InfraRuntimeUnitForm = Loadable(lazy(() => import('pages/admin/InfraRuntimeUnitForm')));
const InfraOperations = Loadable(lazy(() => import('pages/admin/InfraOperations')));
const InfraOperationSchedules = Loadable(lazy(() => import('pages/admin/InfraOperationSchedules')));
const InfraDeploymentPacks = Loadable(lazy(() => import('pages/admin/InfraDeploymentPacks')));
const InfraDeploymentPackForm = Loadable(lazy(() => import('pages/admin/InfraDeploymentPackForm')));
const InfraDeploymentPackVersions = Loadable(lazy(() => import('pages/admin/InfraDeploymentPackVersions')));
const InfraDeploymentPackVersionForm = Loadable(lazy(() => import('pages/admin/InfraDeploymentPackVersionForm')));
const InfraDeploymentPackComponents = Loadable(lazy(() => import('pages/admin/InfraDeploymentPackComponents')));
const InfraDeploymentPackComponentForm = Loadable(lazy(() => import('pages/admin/InfraDeploymentPackComponentForm')));
const InfraTenantBindings = Loadable(lazy(() => import('pages/admin/InfraTenantBindings')));
const InfraTenantBindingForm = Loadable(lazy(() => import('pages/admin/InfraTenantBindingForm')));
const InfraAlerts = Loadable(lazy(() => import('pages/admin/InfraAlerts')));
const InfraReleaseSettings = Loadable(lazy(() => import('pages/admin/InfraReleaseSettings')));
const ContractTemplates = Loadable(lazy(() => import('pages/admin/ContractTemplates')));
const ContractTemplateForm = Loadable(lazy(() => import('pages/admin/ContractTemplateForm')));
const Contracts = Loadable(lazy(() => import('pages/admin/Contracts')));
const ContractForm = Loadable(lazy(() => import('pages/admin/ContractForm')));
const SecurityUsers = Loadable(lazy(() => import('pages/admin/SecurityUsers')));
const SecurityUserForm = Loadable(lazy(() => import('pages/admin/SecurityUserForm')));
const SecurityRoles = Loadable(lazy(() => import('pages/admin/SecurityRoles')));
const SecurityRoleForm = Loadable(lazy(() => import('pages/admin/SecurityRoleForm')));
const SecurityChangePassword = Loadable(lazy(() => import('pages/admin/SecurityChangePassword')));
const UserMailAccountSettings = Loadable(lazy(() => import('pages/admin/UserMailAccountSettings')));
const NotificationTemplates = Loadable(lazy(() => import('pages/admin/NotificationTemplates')));
const NotificationTemplateForm = Loadable(lazy(() => import('pages/admin/NotificationTemplateForm')));
const NotificationRules = Loadable(lazy(() => import('pages/admin/NotificationRules')));
const NotificationRuleForm = Loadable(lazy(() => import('pages/admin/NotificationRuleForm')));
const NotificationDispatches = Loadable(lazy(() => import('pages/admin/NotificationDispatches')));

export default function Router() {
  return useRoutes([
    {
      path: '/',
      element: (
        <GuestGuard>
          <Login />
        </GuestGuard>
      )
    },
    {
      path: 'register',
      element: (
        <GuestGuard>
          <Register />
        </GuestGuard>
      )
    },
    {
      path: 'public/contract-sign/:token',
      element: <ContractSignPublic />
    },
    {
      path: 'public/receivable/:token',
      element: <ReceivablePublic />
    },
    {
      path: 'auth',
      element: <LogoOnlyLayout />,
      children: [
        { path: 'login', element: <Login /> }
      ]
    },
    {
      path: 'kanban-report',
      element: (
        <AuthGuard>
          <KanbanCommercial standaloneReport />
        </AuthGuard>
      )
    },
    {
      path: 'app',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to="/app/dashboard" replace /> },
        { path: 'dashboard', element: <AdminDashboard /> },
        { path: 'client-dashboard', element: <ClientDashboard /> },
        { path: 'clients', element: <Clients /> },
        { path: 'clients/new', element: <ClientForm /> },
        { path: 'clients/edit/:id', element: <ClientForm /> },
        { path: 'cartera', element: <Cartera /> },
        { path: 'cartera-settings', element: <CarteraSettings /> },
        { path: 'countries', element: <Countries /> },
        { path: 'countries/new', element: <CountryForm /> },
        { path: 'countries/edit/:id', element: <CountryForm /> },
        { path: 'states', element: <States /> },
        { path: 'states/new', element: <StateForm /> },
        { path: 'states/edit/:id', element: <StateForm /> },
        { path: 'cities', element: <Cities /> },
        { path: 'cities/new', element: <CityForm /> },
        { path: 'cities/edit/:id', element: <CityForm /> },
        { path: 'currencies', element: <Currencies /> },
        { path: 'currencies/new', element: <CurrencyForm /> },
        { path: 'currencies/edit/:id', element: <CurrencyForm /> },
        { path: 'price-lists', element: <PriceLists /> },
        { path: 'price-lists/new', element: <PriceListForm /> },
        { path: 'price-lists/edit/:id', element: <PriceListForm /> },
        { path: 'products', element: <Products /> },
        { path: 'products/new', element: <ProductForm /> },
        { path: 'products/edit/:id', element: <ProductForm /> },
        { path: 'industries', element: <Industries /> },
        { path: 'industries/new', element: <IndustryForm /> },
        { path: 'industries/edit/:id', element: <IndustryForm /> },
        { path: 'work-areas', element: <WorkAreas /> },
        { path: 'work-areas/new', element: <WorkAreaForm /> },
        { path: 'work-areas/edit/:id', element: <WorkAreaForm /> },
        { path: 'kanban-priorities', element: <KanbanPriorities /> },
        { path: 'kanban-priorities/new', element: <KanbanPriorityForm /> },
        { path: 'kanban-priorities/edit/:id', element: <KanbanPriorityForm /> },
        { path: 'calendar-categories', element: <CalendarCategories /> },
        { path: 'calendar-categories/new', element: <CalendarCategoryForm /> },
        { path: 'calendar-categories/edit/:id', element: <CalendarCategoryForm /> },
        { path: 'area-capacity-calendar', element: <AreaCapacityCalendar /> },
        { path: 'area-calendar', element: <AreaCalendar /> },
        { path: 'area-post-its', element: <AreaPostIts /> },
        { path: 'form-designer-forms', element: <FormDesignerForms /> },
        { path: 'form-designer-forms/new', element: <FormDesignerForm /> },
        { path: 'form-designer-forms/edit/:id', element: <FormDesignerForm /> },
        { path: 'processes', element: <Processes /> },
        { path: 'processes/new', element: <ProcessForm /> },
        { path: 'processes/edit/:id', element: <ProcessForm /> },
        { path: 'requirement-templates', element: <RequirementTemplates /> },
        { path: 'requirement-templates/new', element: <RequirementTemplateForm /> },
        { path: 'requirement-templates/edit/:id', element: <RequirementTemplateForm /> },
        { path: 'requirements', element: <Requirements /> },
        { path: 'requirements/new', element: <RequirementDetail /> },
        { path: 'requirements/:id', element: <RequirementDetail /> },
        { path: 'client-statuses', element: <ClientStatuses /> },
        { path: 'client-statuses/new', element: <ClientStatusForm /> },
        { path: 'client-statuses/edit/:id', element: <ClientStatusForm /> },
        { path: 'company-profile', element: <CompanyProfileForm /> },
        { path: 'storage-config',  element: <StorageConfigPage /> },
        { path: 'document-types', element: <DocumentTypes /> },
        { path: 'document-types/new', element: <DocumentTypeForm /> },
        { path: 'document-types/edit/:id', element: <DocumentTypeForm /> },
        { path: 'quotes', element: <Quotes /> },
        { path: 'quotes/new', element: <QuoteForm /> },
        { path: 'quotes/edit/:id', element: <QuoteForm /> },
        { path: 'quote-proposal-templates', element: <QuoteProposalTemplates /> },
        { path: 'quote-proposal-templates/new', element: <QuoteProposalTemplateForm /> },
        { path: 'quote-proposal-templates/edit/:id', element: <QuoteProposalTemplateForm /> },
        { path: 'quote-variables', element: <QuoteVariableDefinitions /> },
        { path: 'quote-variables/new', element: <QuoteVariableDefinitionForm /> },
        { path: 'quote-variables/edit/:id', element: <QuoteVariableDefinitionForm /> },
        { path: 'quote-formulas', element: <QuoteFormulaDefinitions /> },
        { path: 'quote-formulas/new', element: <QuoteFormulaDefinitionForm /> },
        { path: 'quote-formulas/edit/:id', element: <QuoteFormulaDefinitionForm /> },
        { path: 'client-quote-variables', element: <ClientQuoteVariableValues /> },
        { path: 'contract-templates', element: <ContractTemplates /> },
        { path: 'contract-templates/new', element: <ContractTemplateForm /> },
        { path: 'contract-templates/edit/:id', element: <ContractTemplateForm /> },
        { path: 'contracts', element: <Contracts /> },
        { path: 'contracts/new', element: <ContractForm /> },
        { path: 'contracts/edit/:id', element: <ContractForm /> },
        { path: 'kanban', element: <KanbanCommercial /> },
        { path: 'kanban-my-week', element: <KanbanMyWeek /> },
        { path: 'kanban-card-templates', element: <KanbanCardTemplates /> },
        { path: 'kanban-card-templates/new', element: <KanbanCardTemplateForm /> },
        { path: 'kanban-card-templates/edit/:id', element: <KanbanCardTemplateForm /> },
        { path: 'chat-inbox', element: <ChatInbox /> },
        { path: 'developer/releases', element: <DeveloperReleases /> },
        { path: 'developer/enpiso-api', element: <DeveloperEnpisoApi /> },
        { path: 'developer/release-systems', element: <ReleaseSystems /> },
        { path: 'developer/release-systems/new', element: <ReleaseSystemForm /> },
        { path: 'developer/release-systems/edit/:id', element: <ReleaseSystemForm /> },
        { path: 'developer/infra/dashboard', element: <InfraDashboard /> },
        { path: 'developer/infra/servers', element: <InfraServers /> },
        { path: 'developer/infra/servers/new', element: <InfraServerForm /> },
        { path: 'developer/infra/servers/edit/:id', element: <InfraServerForm /> },
        { path: 'developer/infra/runtime-units', element: <InfraRuntimeUnits /> },
        { path: 'developer/infra/runtime-units/new', element: <InfraRuntimeUnitForm /> },
        { path: 'developer/infra/runtime-units/edit/:id', element: <InfraRuntimeUnitForm /> },
        { path: 'developer/infra/operations', element: <InfraOperations /> },
        { path: 'developer/infra/operation-schedules', element: <InfraOperationSchedules /> },
        { path: 'developer/infra/deployment-packs', element: <InfraDeploymentPacks /> },
        { path: 'developer/infra/deployment-packs/new', element: <InfraDeploymentPackForm /> },
        { path: 'developer/infra/deployment-packs/edit/:id', element: <InfraDeploymentPackForm /> },
        { path: 'developer/infra/deployment-pack-versions', element: <InfraDeploymentPackVersions /> },
        { path: 'developer/infra/deployment-pack-versions/new', element: <InfraDeploymentPackVersionForm /> },
        { path: 'developer/infra/deployment-pack-versions/edit/:id', element: <InfraDeploymentPackVersionForm /> },
        { path: 'developer/infra/deployment-pack-components', element: <InfraDeploymentPackComponents /> },
        { path: 'developer/infra/deployment-pack-components/new', element: <InfraDeploymentPackComponentForm /> },
        { path: 'developer/infra/deployment-pack-components/edit/:id', element: <InfraDeploymentPackComponentForm /> },
        { path: 'developer/infra/tenant-bindings', element: <InfraTenantBindings /> },
        { path: 'developer/infra/tenant-bindings/new', element: <InfraTenantBindingForm /> },
        { path: 'developer/infra/tenant-bindings/edit/:id', element: <InfraTenantBindingForm /> },
        { path: 'developer/infra/alerts', element: <InfraAlerts /> },
        { path: 'developer/infra/settings', element: <InfraReleaseSettings /> },
        { path: 'developer/storage-config', element: <StorageConfigPage /> },
        { path: 'security/users', element: <SecurityUsers /> },
        { path: 'security/users/new', element: <SecurityUserForm /> },
        { path: 'security/users/edit/:id', element: <SecurityUserForm /> },
        { path: 'security/roles', element: <SecurityRoles /> },
        { path: 'security/roles/new', element: <SecurityRoleForm /> },
        { path: 'security/roles/edit/:id', element: <SecurityRoleForm /> },
        { path: 'security/change-password', element: <SecurityChangePassword /> },
        { path: 'mail-account', element: <UserMailAccountSettings /> },
        { path: 'notification-templates', element: <NotificationTemplates /> },
        { path: 'notification-templates/new', element: <NotificationTemplateForm /> },
        { path: 'notification-templates/edit/:id', element: <NotificationTemplateForm /> },
        { path: 'notification-rules', element: <NotificationRules /> },
        { path: 'notification-rules/new', element: <NotificationRuleForm /> },
        { path: 'notification-rules/edit/:id', element: <NotificationRuleForm /> },
        { path: 'notification-dispatches', element: <NotificationDispatches /> }
      ]
    },
    { path: '*', element: <Navigate to="/" replace /> }
  ]);
}
